import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import authService from '../services/authService';
import cartService from '../services/cartService';

const CartContext = createContext();

const CART_STORAGE_KEY = 'yiatou_cart';

// Charge le panier persisté localement (survit au rafraîchissement, sert de panier invité)
const loadInitialCart = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.warn('Erreur lors du chargement du panier:', e);
  }
  return [];
};

const isLoggedIn = () => !!authService.getToken?.();

// Fusionne deux paniers : union par id, additionne les quantités pour un même article
const mergeCarts = (a = [], b = []) => {
  const map = new Map();
  const add = (items) => items.forEach((it) => {
    if (!it || it.id == null) return;
    const existing = map.get(it.id);
    if (existing) {
      map.set(it.id, {
        ...existing,
        ...it,
        quantity: (existing.quantity || 1) + (it.quantity || 1),
      });
    } else {
      map.set(it.id, { ...it });
    }
  });
  add(a);
  add(b);
  return Array.from(map.values());
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(loadInitialCart);
  const hasSyncedRef = useRef(false);
  const saveTimer = useRef(null);

  // Persistance locale à chaque changement
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (e) {
      console.warn('Erreur lors de la sauvegarde du panier:', e);
    }
  }, [cartItems]);

  // Synchronise avec le panier serveur (lié au client connecté)
  const syncWithServer = useCallback(async ({ merge }) => {
    if (!isLoggedIn()) return;
    try {
      const serverItems = await cartService.getCart();
      const localItems = loadInitialCart();
      const next = merge
        ? mergeCarts(localItems, serverItems)
        : (serverItems.length ? serverItems : localItems);

      setCartItems(next);
      hasSyncedRef.current = true;

      // Après une fusion (ou si le serveur était vide), on pousse le résultat
      if (merge || serverItems.length === 0) {
        cartService.saveCart(next).catch(() => {});
      }
    } catch {
      // Hors-ligne / erreur : on garde le panier local et on autorise les sauvegardes
      hasSyncedRef.current = true;
    }
  }, []);

  // Au montage : adopter le panier serveur si déjà connecté ; à la connexion : fusionner
  useEffect(() => {
    syncWithServer({ merge: false });
    const onLogin = () => syncWithServer({ merge: true });
    window.addEventListener('auth:login', onLogin);
    return () => window.removeEventListener('auth:login', onLogin);
  }, [syncWithServer]);

  // Sauvegarde serveur debouncée à chaque changement (une fois la synchro initiale faite)
  useEffect(() => {
    if (!isLoggedIn() || !hasSyncedRef.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      cartService.saveCart(cartItems).catch(() => {});
    }, 600);
    return () => clearTimeout(saveTimer.current);
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);

      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prevItems, { ...product, quantity }];
    });
  };

  const updateQuantity = (productId, quantity) => {
    setCartItems(prevItems =>
      prevItems
        .map(item =>
          item.id === productId
            ? { ...item, quantity: Math.max(0, quantity) }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  // Retire plusieurs articles (ex: après une commande réussie sur une sélection)
  const removeItems = (productIds = []) => {
    if (!Array.isArray(productIds) || productIds.length === 0) return;
    const ids = new Set(productIds);
    setCartItems(prevItems => prevItems.filter(item => !ids.has(item.id)));
  };

  // Vide entièrement le panier (local + serveur via la sauvegarde debouncée)
  const clearCart = () => {
    setCartItems([]);
  };

  const updateCartItem = (productId, fields) => {
    setCartItems(prevItems =>
      prevItems.map(item => item.id === productId ? { ...item, ...fields } : item)
    );
  };

  const isInCart = (productId) => cartItems.some(item => item.id === productId);

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      updateQuantity,
      removeFromCart,
      removeItems,
      clearCart,
      updateCartItem,
      isInCart,
      getCartTotal,
      getCartCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
